import { PdfSize, PdfRect, PdfPoint } from '../pdf';
import { calcOrigin } from '../../core/genHelper';

const GRID_CONFIGS = [
  { count: 3, cols: 3, rows: 1 },
  { count: 4, cols: 2, rows: 2 },
  { count: 5, cols: 3, rows: 2 },
  { count: 6, cols: 3, rows: 2 },
  { count: 8, cols: 4, rows: 2 },
  { count: 9, cols: 3, rows: 3 },
  { count: 10, cols: 5, rows: 2 },
  { count: 12, cols: 4, rows: 3 },
  { count: 15, cols: 5, rows: 3 },
  { count: 16, cols: 4, rows: 4 },
  { count: 18, cols: 6, rows: 3 },
  { count: 20, cols: 5, rows: 4 },
  { count: 24, cols: 6, rows: 4 },
];

export function calcIdealGrid(count: number): { cols: number; rows: number } {
  if (count <= 3) return GRID_CONFIGS[0];
  if (count <= 4) return GRID_CONFIGS[1];
  if (count <= 5) return GRID_CONFIGS[2];
  if (count <= 6) return GRID_CONFIGS[3];
  if (count <= 8) return GRID_CONFIGS[4];
  if (count <= 9) return GRID_CONFIGS[5];
  if (count <= 10) return GRID_CONFIGS[6];
  if (count <= 12) return GRID_CONFIGS[7];
  if (count <= 15) return GRID_CONFIGS[8];
  if (count <= 16) return GRID_CONFIGS[9];
  if (count <= 18) return GRID_CONFIGS[10];
  if (count <= 20) return GRID_CONFIGS[11];
  return GRID_CONFIGS[12];
}

export function calcSquareishGrid(count: number): { cols: number; rows: number } {
  if (count <= 0) return { cols: 1, rows: 1 };

  let best = { cols: 1, rows: count };
  let bestScore = Infinity;

  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    const aspectRatio = cols / rows;
    const diff = Math.abs(cols - rows);
    const emptyCells = cols * rows - count;
    const score = diff + emptyCells * 0.1;

    if (score < bestScore) {
      bestScore = score;
      best = { cols, rows };
    }
  }

  return best;
}

export interface GridRectsResult {
  cols: number;
  rows: number;
  origin: PdfPoint;
  cellSize: PdfSize;
  rects: PdfRect[];
}

export function calcGridRects(count: number, margin: number, pageSize: PdfSize): GridRectsResult {
  const { cols, rows } = calcIdealGrid(count);
  const { origin, size } = calcOrigin(cols, rows, margin, pageSize);

  const rects: PdfRect[] = [];
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const idx = y * cols + x;
      if (idx < count) {
        rects.push(
          new PdfRect(
            origin.x + x * size.width,
            origin.y + y * size.height,
            size.width,
            size.height
          )
        );
      }
    }
  }

  return { cols, rows, origin, cellSize: size, rects };
}

export function calcGridRectsEx(count: number, rect: PdfRect, margin = 0): GridRectsResult {
  const isLandscape = rect.width > rect.height;
  const { cols: baseCols, rows: baseRows } = calcSquareishGrid(count);

  const cols = isLandscape ? baseRows : baseCols;
  const rows = isLandscape ? baseCols : baseRows;

  const w = (rect.width - 2 * margin) / cols;
  const h = (rect.height - 2 * margin) / rows;
  const cellSize = new PdfSize(w, h);

  const origin = new PdfPoint(
    rect.x + margin + (rect.width - 2 * margin - w * cols) / 2,
    rect.y + margin + (rect.height - 2 * margin - h * rows) / 2
  );

  const rects: PdfRect[] = [];
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const idx = y * cols + x;
      if (idx < count) {
        rects.push(
          new PdfRect(
            origin.x + x * cellSize.width,
            origin.y + y * cellSize.height,
            cellSize.width,
            cellSize.height
          )
        );
      }
    }
  }

  return { cols, rows, origin, cellSize, rects };
}
